import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteIcon, DirectoryIcon } from 'src/images';
import { SingleTagIcon } from 'src/components/tags/tagsIcons';
import { Knowledge } from './knowledge.component';
import styles from './knowledge.module.scss';
import { knowledgeSetItemDetails } from 'src/store/actions/knowledgeViewer.actions';
import { colorPalette } from 'shared/constants/theme';
import { updateKnowledgeItem } from 'shared/store/actions/knowledge.items.actions';
import { getLang } from 'shared/store/selectors/lang.selector';
import classNames from 'classnames';
import {
  haveEditPermissions,
  getCategoryById,
  getItemCategory,
} from 'shared/store/selectors/knowledge.selector';
import { isExternalSyncReadOnly } from 'shared/utils/knowledge.utils';
import { IconButton } from 'src/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faXmark } from '@fortawesome/pro-solid-svg-icons';

const KnowledgeWrapperComponent = ({
  onClickEvent = () => {},
  item = {},
  linkOwnerIds = [],
  categoryId = null,
  selectItem = () => {},
  removeItem = () => {},
  search = false,
  selected = false,
  allowMove = false,
  editMode = false,
  adminMode = false,
  onDelete = () => {},
  onUpdate = () => {},
  onMove = () => {},
  linkedItem = false,
  hideFavorites = false,
  hideInfo = false,
  preventOpen = false,
  observeRef,
  ...props
}) => {
  const dispatch = useDispatch();

  const hasEditPermission = useSelector(state =>
    haveEditPermissions(state, {
      linked: item?.linked || false,
      linkOwnerIds: linkOwnerIds,
      ownerIds: item?.ownerIds || [],
    }),
  );

  // The sync stamps the category, so an item inside a synced binder carries no flag of
  // its own. Binder rows pass `categoryId`; search results don't, so fall back to
  // locating the item's category — but only in admin mode, since that scan exists purely
  // to gate the admin-only Delete/Move buttons. Selects a boolean, not the category, so
  // the result stays reference-stable across store updates.
  const isParentSynced = useSelector(state => {
    if (categoryId) {
      return isExternalSyncReadOnly(getCategoryById(state.knowledge, { id: categoryId })?.options);
    }
    if (!adminMode) return false;
    return isExternalSyncReadOnly(getItemCategory(state, { id: item?.id })?.options);
  });
  const isReadOnly = isExternalSyncReadOnly(item?.options) || isParentSynced;

  const lang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));

  const openInformation = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      dispatch(knowledgeSetItemDetails(item?.id, linkOwnerIds, categoryId));
    },
    [item, dispatch, linkOwnerIds, categoryId],
  );

  const select = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      selectItem(item);
    },
    [item, selectItem],
  );

  const unselect = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      removeItem(item);
    },
    [item, removeItem],
  );

  const onMoveAction = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      if (!hasEditPermission) {
        return;
      }
      onMove({ id: item.id, title: item.title, linkOwnerIds, ownerIds: item.ownerIds });
    },
    [hasEditPermission, onMove, item, linkOwnerIds],
  );

  const onDeleteAction = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      if (!hasEditPermission) {
        return;
      }
      onDelete({ id: item.id, title: item.title });
    },
    [hasEditPermission, onDelete, item],
  );

  const onExternalUpdateAction = useCallback(
    async dataToUpdate => {
      await dispatch(updateKnowledgeItem({ ...item, ...dataToUpdate }));
    },
    [item, dispatch],
  );

  const moveTooltip = hasEditPermission
    ? linkedItem
      ? lang.MOVE_LINK
      : lang.MOVE
    : lang.NOT_ALLOWED_TO_EDIT;

  const actionButtons = () => {
    if (editMode) {
      return (
        <>
          {item?.tags?.specialty && <SingleTagIcon tag={item?.tags?.specialty} />}
          {search && (
            <IconButton label={lang.ADD} onClick={select} className={styles.buttonWrapper}>
              <FontAwesomeIcon icon={faArrowRight} color={colorPalette.primaryMain7} />
            </IconButton>
          )}
          {selected && (
            <IconButton label={lang.REMOVE} onClick={unselect} className={styles.buttonWrapper}>
              <FontAwesomeIcon icon={faXmark} size="lg" color={colorPalette.primaryMain7} />
            </IconButton>
          )}
        </>
      );
    }
    return (
      <>
        <IconButton
          label={
            isReadOnly
              ? lang.READ_ONLY_SYNCED
              : hasEditPermission
                ? lang.DELETE
                : lang.NOT_ALLOWED_TO_EDIT
          }
          onClick={onDeleteAction}
          className={styles.buttonWrapper}
          disabled={!hasEditPermission || isReadOnly}>
          <DeleteIcon
            className={classNames(
              styles.deleteIcon,
              hasEditPermission && !isReadOnly ? '' : styles.disabled,
            )}
          />
        </IconButton>
        <IconButton
          label={isReadOnly ? lang.READ_ONLY_SYNCED : moveTooltip}
          onClick={onMoveAction}
          className={styles.buttonWrapper}
          disabled={!allowMove || !hasEditPermission || isReadOnly}>
          <DirectoryIcon className={styles.changeDirectory} />
        </IconButton>
      </>
    );
  };

  if (!linkedItem) {
    return (
      <Knowledge
        key={item.id}
        {...item}
        adminMode={adminMode}
        readOnly={isReadOnly}
        deleteKnowledge={onDelete}
        editKnowledge={onUpdate}
        moveKnowledgeAction={onMove}
        openInformation={openInformation}
        actionButtons={actionButtons}
        hideFavorites={hideFavorites}
        hideInfo={hideInfo}
        externalUpdateAction={onExternalUpdateAction}
        onClickEvent={onClickEvent}
        preventOpen={preventOpen}
        lang={lang}
        observeRef={observeRef}
        {...props}
      />
    );
  }
  return (
    <Knowledge
      key={item.id}
      {...item}
      actionButtons={actionButtons}
      adminMode={adminMode}
      readOnly={isReadOnly}
      draggable={!editMode}
      hideFavorites={hideFavorites}
      hideInfo={hideInfo}
      openInformation={openInformation}
      onClickEvent={onClickEvent}
      lang={lang}
      preventOpen={preventOpen}
      observeRef={observeRef}
      {...props}
    />
  );
};

export { KnowledgeWrapperComponent };
